import React, { useState, useEffect, useMemo } from "react";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import usePostCommand from "../../../customHooks/POST_requests/usePostCommand";
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// COMPONENTS
import ActionCommandButton from "../Actions/components/ActionCommandButton";
// INTERFACES
import { RootState } from "../../../app/store";
import { ActionCommandOption } from "../../../interfaces/Bank.interface";
// CONSTANTS
import {
  resolveCommandEnum,
  assertBoundCommand,
  UNBOUND_COMMAND_REASON,
  UNBOUND_PAIR_REASON,
  dispatchFloor,
  floorSelectionMissing,
} from "./actionButtonUtils";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
// VERIFICATION
import useActionOutcome, { ActionVerification } from "./useActionOutcome";

export default function ActionButtonOnRelease({
  validStates,
  label,
  toggleLabel,
  messageId,
  toggleMessageId,
  command = null,
  toggleCommand = null,
  colorAction,
  timerAction,
  targetFloor = undefined,
  expectedResponse = null,
  expectedResponseTimeout = null,
  commandMessageId = null,
}: {
  validStates: string[];
  label: string;
  toggleLabel: string;
  messageId: string;
  toggleMessageId: string;
  command?: ActionCommandOption | null;
  toggleCommand?: ActionCommandOption | null;
  colorAction: string;
  timerAction: number;
  /**
   * The floor this row's commands dispatch to. `undefined` for a command that
   * takes no floor (dispatches at 0, as before); `null` while a floor-taking
   * command is still waiting for the operator to pick one. See dispatchFloor.
   */
  targetFloor?: number | null;
} & ActionVerification) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [loading, setLoading] = useState(false);
  const [counter, setCounter] = useState(0);
  const [buttonWasClicked, setButtonWasClicked] = useState(false);
  const [lastCommandSent, setLastCommandSent] = useState<string>("ON");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { triggerPostCommand, isLoadingPostCommand } = usePostCommand();
  const { messages } = useGetAllMessages();
  const reportOutcome = useActionOutcome({
    expectedResponse,
    expectedResponseTimeout,
    commandMessageId,
  });

  // ************************************************ */
  // MEMOIZED VALUES ******************************** */
  // null (not 0) when the message is unbound — enum 0 is a real Control
  // (CarCallLock), so a 0 fallback would mis-fire that command.
  const messageEnum = useMemo(
    () => resolveCommandEnum(messages, messageId, command),
    [messages, messageId, command],
  );

  const toggleMessageEnum = useMemo(
    () => resolveCommandEnum(messages, toggleMessageId, toggleCommand),
    [messages, toggleMessageId, toggleCommand],
  );

  // NOTE: NEITHER valid-state list is resolved here. HOLDBACK never gated its
  // primary command on the car's state — the old code resolved `validStates`
  // only to pick a CSS class — and turning it into a disable rule now would
  // change when the command can be sent. `toggleValidStates` was resolved only
  // by the post-command confirmation sweep, which could not run at all on the
  // EDGE/OFFLINE path; verification now comes from the action's declared
  // expected response instead of from guessing at the service mode.

  // ************************************************ */
  // FUNCTIONS ************************************** */
  // Send the action's BOUND command, never a hardcoded Control value.
  const launchTimer = () => {
    // Update counter
    let count = 0;
    const countdownInterval = setInterval(() => {
      // eslint-disable-next-line operator-assignment
      count = count + 1;
      setCounter(count);
      if (count === timerAction) {
        clearInterval(countdownInterval);
        setLoading(false);
      }
    }, 1000); // Update counter every second
  };

  const handleMouseUp = async () => {
    if (buttonWasClicked && lastCommandSent === "ON") {
      setLoading(false);
      setLastCommandSent("OFF");
      setButtonWasClicked(false);
      if (!assertBoundCommand(toggleMessageEnum, toggleLabel, theme)) {
        reportOutcome(toggleLabel, false, toggleMessageId ?? null);
        return;
      }
      const delivered = await triggerPostCommand({
        floor: dispatchFloor(targetFloor),
        command: toggleMessageEnum,
        liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
        active: true,
      });
      reportOutcome(toggleLabel, delivered, toggleMessageId ?? null);
    }
  };

  // The hold countdown starts on DELIVERY. It used to be driven by
  // `isSuccessPostCommand`, the EMULATOR mutation's flag, which is permanently
  // false on the EDGE and OFFLINE paths — so the counter never advanced and the
  // timer-elapsed release never fired on any production install.
  const onClickButton = async () => {
    if (!assertBoundCommand(messageEnum, label, theme)) {
      reportOutcome(label, false, messageId);
      return;
    }
    setLastCommandSent("ON");
    setButtonWasClicked(true);
    setLoading(true);
    const delivered = await triggerPostCommand({
      floor: dispatchFloor(targetFloor),
      command: messageEnum,
      liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
      active: true,
    });
    reportOutcome(label, delivered, messageId);
    if (delivered) {
      launchTimer();
      return;
    }
    // Nothing went out, so there is nothing to release. Leaving `loading` and
    // `buttonWasClicked` set would arm a paired command for a primary that was
    // never sent.
    setLoading(false);
    setButtonWasClicked(false);
    setLastCommandSent("OFF");
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (counter === timerAction) {
      handleMouseUp();
    }
  }, [counter]);

  // ************************************************ */
  // COMPONENT ************************************** */
  // The press-and-hold interaction is untouched: mouse-down sends the primary
  // command, and the paired command goes out on release or when the timer
  // elapses (see the `counter` effect above).
  //
  // A floor-taking command must not dispatch until the operator picks a floor
  // on the row's strip; `targetFloor` is null for exactly as long as that is
  // true. The gate is VISIBLE — a disabled button naming its reason — rather
  // than a press that silently sends nothing, and rather than the position-0
  // dispatch it replaces, which aimed a positional command at a floor the
  // operator never chose.
  const awaitingFloor = floorSelectionMissing(targetFloor);
  const selectFloorReason = t(
    "monitoringActions.selectFloorFirst",
    "Select a floor for this command first",
  );

  // Both legs must resolve before the primary may be pressed: the primary's
  // own press is what dispatches the pair, so a missing pair could only be
  // reported after the primary had already gone out.
  const pairUnbound = messageEnum == null || toggleMessageEnum == null;
  const primaryReason = (): string | undefined => {
    if (pairUnbound) return UNBOUND_PAIR_REASON;
    if (awaitingFloor) return selectFloorReason;
    return undefined;
  };

  return (
    <>
      <ActionCommandButton
        label={label}
        color={colorAction}
        loading={loading || isLoadingPostCommand}
        disabled={pairUnbound || awaitingFloor || loading || isLoadingPostCommand}
        disabledReason={primaryReason()}
        onMouseDown={onClickButton}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
      {/* The paired command has no control of its own — releasing the primary
          sends it — but it is still one of the two commands this row runs, so
          it gets a permanently inert button instead of no button at all. The
          countdown lives here, on the command that is pending; it used to
          replace the PRIMARY button's label, which read as though the primary
          were about to fire again. */}
      <ActionCommandButton
        label={toggleLabel}
        countdown={loading ? counter : null}
        disabled
        disabledReason={
          toggleMessageEnum == null
            ? UNBOUND_COMMAND_REASON
            : t(
                "monitoringActions.sentOnRelease",
                "Sent when the button is released, or when the countdown ends",
              )
        }
      />
    </>
  );
}
